'use client'

import {useState} from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'

import {SoftwareIndicatorRadarPlotChartjs} from '~/components/software/indicator/SoftwareIndicatorRadarPlot'
import {useSoftwareEvaluation} from '~/components/software/indicator/useSoftwareEvaluation'
import {useSoftwareEvaluationContext} from '~/components/software/indicator/SoftwareQualityEvaluationContext'
import {PolygonData, SoftwareIndicatorAttribute} from '~/components/software/indicator/apiSoftwareIndicator'
import {SoftwareEvaluationResult} from '~/components/software/indicator/apiSoftwareIndicator'
import {useSoftwareIndicatorConfiguration} from '~/components/software/indicator/useSoftwareIndicatorConfiguration'
import Chip from '@mui/material/Chip'

type EvaluatedSoftwareTableProps = {
  data: {
    software_id: string
    software_slug: string
    software_name: string
  }[]
  year: string
}

type EvaluatedSoftwareTableRowProps = {
  id: string
  year: string
  slug: string
  name: string
}

export function EvaluationStatusBadge({evaluationStatus}: {evaluationStatus: SoftwareEvaluationResult | null | undefined}) {

  function statusColor() {
    switch (evaluationStatus) {
      case 'undefined':
        return '#fa7833'
      case 'counted':
        return '#8cd600'
      case 'not counted':
        return '#ed5ef2'
      default:
        return 'white'
    }
  }

  return <Chip label={evaluationStatus} sx={{bgcolor: statusColor()}} size='small' />
}

type MinimalScoreTableProps = {
  minimalPolygon: PolygonData[],
  handleMinimalScoreUpdate: (index: number, value: number) => void
}

export function EvaluatedSoftwareTableRow({id, slug, name, year}: EvaluatedSoftwareTableRowProps) {
  const {evaluationResult} = useSoftwareEvaluation({software: id, year: year})

  return (
    <TableRow>
      <TableCell sx={{width: '50%', fontSize: '1rem'}} align="left">
        <Button variant='text' href={`/software/${slug}/indicator`}>{name}</Button>
      </TableCell>
      <TableCell sx={{width: '30%', fontSize: '1rem'}}>{id}</TableCell>
      <TableCell sx={{width: '20%', fontSize: '1rem'}} align="right">
        {evaluationResult ? <EvaluationStatusBadge evaluationStatus={evaluationResult} /> : null}
      </TableCell>
    </TableRow>
  )
}

export function EvaluatedSoftwareTable({data, year}: EvaluatedSoftwareTableProps) {

  if (data.length === 0) { return 'No data available' }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left" sx={{fontWeight:900, fontSize: '1.2rem', width: '50%'}}>Software</TableCell>
            <TableCell sx={{fontWeight:900, fontSize: '1.2rem', width: '30%'}}>Software ID</TableCell>
            <TableCell align="right" sx={{fontWeight:900, fontSize: '1.2rem', width: '20%'}}>Evaluation Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          { data.map((software) => (
            <EvaluatedSoftwareTableRow
              key={software.software_id}
              id={software.software_id}
              slug={software.software_slug}
              name={software.software_name}
              year={year}
            />
          ))
          }
        </TableBody>
      </Table>
    </TableContainer>
  )
}

type AttributeWeightEditorProps = {
  dimension: string
  attributes: {id: SoftwareIndicatorAttribute['id'], name: SoftwareIndicatorAttribute['name'], weight: SoftwareIndicatorAttribute['weight']}[]
  saveAttributeWeights: (attributes: {id: SoftwareIndicatorAttribute['id'], name: SoftwareIndicatorAttribute['name'], weight: SoftwareIndicatorAttribute['weight']}[]) => void
}

export function AttributeWeightEditor({dimension, attributes, saveAttributeWeights}: AttributeWeightEditorProps) {
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    setOpen(false)
    saveAttributeWeights(attributes)
  }

  const setWeightFromText = (attribute_id: SoftwareIndicatorAttribute['id'], weight: string) => {
    const weightTruncated = Number(Number(weight).toFixed(1))
    attributes.find(attribute => attribute.id === attribute_id)!.weight = weightTruncated
  }

  return (
    <span>
      <Button onClick={() => setOpen(true)}>Edit Attribute Weights</Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        slotProps={{
          paper: {
            sx: {
              minWidth: '70vw',
            }
          }
        }}
      >
        <DialogTitle>{dimension}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell key="attribute">Attribute</TableCell>
                  <TableCell key="weight">Weight</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attributes.map((attribute) => (
                  <TableRow key={attribute.id}>
                    <TableCell key="attribute">{attribute.name}</TableCell>
                    <TableCell key="weight">
                      <TextField
                        size="small"
                        variant="outlined"
                        key={attribute.weight}
                        defaultValue={attribute.weight}
                        onBlur={(ev) => setWeightFromText(attribute.id, ev.target.value)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            const target = ev.target as HTMLInputElement
                            setWeightFromText(attribute.id, target.value)
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>save</Button>
        </DialogActions>
      </Dialog>
    </span>
  )
}

export function MinimalScoreTable({minimalPolygon, handleMinimalScoreUpdate}: MinimalScoreTableProps) {
  const [force, setForce] = useState(0)

  const setMinimalScoreFromText = (dimension_id: number, value: string) => {
    const level = Number(Number(value).toFixed(1))
    if (value === null || isNaN(level)) {
      return
    }
    handleMinimalScoreUpdate(dimension_id, Math.max(Math.min(level, 5), 0))
    setForce(force + 1)
  }

  const ctx = useSoftwareEvaluationContext()

  const saveAttributeWeights = (attributes: {id: SoftwareIndicatorAttribute['id'], name: SoftwareIndicatorAttribute['name'], weight: SoftwareIndicatorAttribute['weight']}[]) => {
    ctx.updateAttributes(attributes)
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{fontWeight: 900, fontSize: '1.0rem'}} key="dimension">Dimension</TableCell>
            <TableCell sx={{fontWeight: 900, fontSize: '1.0rem'}} key="level" align='center'>Minimum required level</TableCell>
            <TableCell sx={{fontWeight: 900, fontSize: '1.0rem'}} key="weights"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody key={force}>
          {minimalPolygon.map((dimension) => (
            <TableRow key={JSON.stringify(dimension)}>
              <TableCell key="dimension">{dimension.name}</TableCell>
              <TableCell key="level" align='center'>
                <TextField
                  size="small"
                  variant="outlined"
                  key={dimension.id}
                  defaultValue={dimension.minimum_required_level}
                  onBlur={(ev) => setMinimalScoreFromText(dimension.id, ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      const target = ev.target as HTMLInputElement
                      setMinimalScoreFromText(dimension.id, target.value)
                    }
                  }}
                />
              </TableCell>
              <TableCell key="weights" align='right'>
                <AttributeWeightEditor
                  dimension={dimension.name}
                  attributes={ctx.getIndicatorAttributes(dimension.id)}
                  saveAttributeWeights={saveAttributeWeights}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default function EditSoftwareQualityIndicator({year}: {year: string}) {
  const ctx = useSoftwareEvaluationContext()
  const {minimalPolygon, updateMinimalScores} = useSoftwareIndicatorConfiguration(year)

  const handleMinimalScoreUpdate = (dimension_id: number, value: number) => {
    updateMinimalScores([{id: dimension_id, minimum_required_level: value}])
  }

  return (
    <div className='flex flex-col gap-4'>
      <h2>Set Minimal Polygon</h2>
      <p>Adjust the minimal scores for each dimension of the software quality indicator. This minimal polygon represents the dimension-specific requirements for software to be counted. </p>
      <Alert severity="info" sx={{margin:'0.5rem 0 1rem 0'}}>
        <AlertTitle sx={{fontWeight:500}}>Drag to update</AlertTitle>
        <p>
          To change the minimum score of a dimension, just grab and drag the corresponding data point in the chart below. You can also adjust the values in the table beneath the chart.
        </p>
      </Alert>
      <div className='w-full h-[50vh]'>
        <SoftwareIndicatorRadarPlotChartjs score_polygon={[]} minimal_polygon={minimalPolygon} display_scores={true} callbackOnDrag={handleMinimalScoreUpdate}/>

      </div>
      <MinimalScoreTable minimalPolygon={minimalPolygon} handleMinimalScoreUpdate={handleMinimalScoreUpdate} />
      <h2 className='mt-10'>Evaluated Software</h2>
      <p className='mb-6'>The table below lists all software entries that are at least partially evaluated.</p>
      <EvaluatedSoftwareTable key={JSON.stringify(minimalPolygon)} data={ctx.evaluatedSoftware} year={year} />
    </div>
  )
}
